/**
 * Admin Dashboard - Layout con Sidebar
 * Sprint 21: Permisos Granulares
 */

import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Users,
  LogOut,
  Shield,
  LayoutDashboard,
  Mic2,
  Settings,
  QrCode,
  Menu,
  FileText,
  Mail,
  Lock as LockIcon,
  Clock,
  ClipboardList,
  MonitorCog,
  FileSearch,
  UserCheck,
  Calendar,
  Loader2,
  Database,
  LifeBuoy,
  BookOpenText,
} from "lucide-react";
import api from "../src/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "./ui/select";

// Import components
import { RegistrationTable } from "./registration-table";
import { MetricCard } from "./admin/MetricCard";
import { PERMISSIONS, hasPermission } from "../src/hooks/usePermission";
import { EventsManagementView } from "./admin/EventsManagementView";
import { UsersManagementView } from "./admin/UsersManagementView";
import { HelpDeskView } from "./admin/HelpDeskView";
import { SimulationView } from "./admin/SimulationView";
import { SettingsPage } from "@/pages/admin/SettingsPage";
import { MailTemplatesPage } from "@/pages/admin/MailTemplatesPage";
import { SyswebMonitorPage } from "@/pages/admin/SyswebMonitorPage";
import { PaperEvaluationMonitorPage } from "@/pages/admin/PaperEvaluationMonitorPage";
import { RegistrationMonitorPage } from "@/pages/admin/RegistrationMonitorPage";
import { AuditLogsPage } from "@/pages/admin/AuditLogsPage";
import { CommitteePapersPage } from "@/pages/admin/committee/papers";
import { CommitteeDashboardPage } from "@/pages/committee/dashboard";
import { CommitteeEvaluatorsPage } from "@/pages/committee/evaluators";
import { SessionDebugPanel } from "./debug/SessionDebugPanel";
import { buildPath, matchPath } from "../src/lib/routing";

interface AdminDashboardProps {
  onBackToRegister: () => void;
}

interface Edition {
  id: string;
  year: number;
  name: string | null;
  isActive: boolean;
}

interface Event {
  id: string;
  editionId: string;
  name: string;
  slug: string;
  axis: string;
  syswebId?: string | null;
}

interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  fatherLastName: string | null;
  motherLastName: string | null;
  name: string;
  isSuperAdmin: boolean;
  permissions?: string[];
  globalPermissions?: string[];
  assignedEventIds?: string[];
  permissionsByEvent?: Record<string, string[]>;
}

interface Metrics {
  total: number;
  attendees: number;
  speakers: number;
  pending: number;
}

type AxisFilter = 'ALL' | 'cientifico' | 'cultural' | 'innovacion';
const PAPER_MONITOR_AXIS = 'cientifico';
const SPECIAL_GENERAL_ACCESS_SYSWEB_IDS = new Set(['202600210', '202600207']);

type ViewState =
  | 'overview'
  | 'registrations'
  | 'validation'
  | 'events'
  | 'users'
  | 'settings'
  | 'mailTemplates'
  | 'syswebMonitor'
  | 'paperMonitor'
  | 'registrationMonitor'
  | 'auditLogs'
  | 'papers'
  | 'committee'
  | 'committeeDashboard'
  | 'committeeEvaluators'
  | 'helpdesk'
  | 'simulation';

function hasRawPermission(userPermissions: string[] | undefined, permission: string): boolean {
  if (!userPermissions) return false;
  if (userPermissions.includes('*')) return true;
  return userPermissions.includes(permission);
}

export function AdminDashboard({ onBackToRegister }: AdminDashboardProps) {
  const [activeView, setActiveView] = useState<ViewState>("overview");
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  // Layout State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Multi-Year Context
  const [editions, setEditions] = useState<Edition[]>([]);
  const [selectedEditionId, setSelectedEditionId] = useState<string>('');
  const [isLoadingEditions, setIsLoadingEditions] = useState(true);

  // Multi-Event Context
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [selectedAxisFilter, setSelectedAxisFilter] = useState<AxisFilter>('ALL');

  // Metrics
  const [metrics, setMetrics] = useState<Metrics>({ total: 0, attendees: 0, speakers: 0, pending: 0 });
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const eventPermissions = currentUser?.permissionsByEvent?.[selectedEventId];
  const canViewSettings =
    Boolean(currentUser?.isSuperAdmin) ||
    hasRawPermission(eventPermissions, 'can_view_settings') ||
    hasPermission(eventPermissions, PERMISSIONS.MANAGE_SETTINGS);
  const canManageSettings =
    Boolean(currentUser?.isSuperAdmin) || hasPermission(eventPermissions, PERMISSIONS.MANAGE_SETTINGS);
  const canViewSyswebMonitor =
    hasRawPermission(currentUser?.permissions, '*') || hasRawPermission(currentUser?.globalPermissions, PERMISSIONS.VIEW_SYSWEB_MONITOR);
  const hasGlobalPaperMonitor = hasRawPermission(currentUser?.globalPermissions, PERMISSIONS.VIEW_PAPER_MONITOR);
  const hasGlobalHelpdesk = hasRawPermission(currentUser?.globalPermissions, PERMISSIONS.GLOBAL_HELPDESK);
  const canViewPaperMonitor =
    hasRawPermission(currentUser?.permissions, '*') ||
    hasPermission(currentUser?.permissions, PERMISSIONS.VIEW_PAPER_MONITOR) ||
    hasGlobalPaperMonitor;
  const canViewRegistrationMonitor =
    hasRawPermission(currentUser?.permissions, '*') ||
    hasPermission(currentUser?.permissions, PERMISSIONS.VIEW_REGISTRATION_MONITOR) ||
    hasRawPermission(currentUser?.globalPermissions, PERMISSIONS.VIEW_REGISTRATION_MONITOR);
  const canViewAuditLogs =
    hasRawPermission(currentUser?.permissions, '*') ||
    hasRawPermission(currentUser?.globalPermissions, PERMISSIONS.VIEW_AUDIT_LOGS);
  const canSendEvaluatorRecognitions =
    hasRawPermission(currentUser?.permissions, '*') ||
    hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.SEND_EVALUATOR_RECOGNITIONS);
  const paperMonitorEvents = currentUser?.permissions?.includes('*') || hasGlobalPaperMonitor
    ? filteredEvents.filter(isPaperMonitorEvent)
    : filteredEvents.filter((event) =>
      isPaperMonitorEvent(event) && hasPermission(currentUser?.permissionsByEvent?.[event.id], PERMISSIONS.VIEW_PAPER_MONITOR),
    );
  const registrationMonitorEvents = currentUser?.permissions?.includes('*') || hasRawPermission(currentUser?.globalPermissions, PERMISSIONS.VIEW_REGISTRATION_MONITOR)
    ? filteredEvents
    : filteredEvents.filter((event) =>
      hasPermission(currentUser?.permissionsByEvent?.[event.id], PERMISSIONS.VIEW_REGISTRATION_MONITOR),
    );

  // Responsive check
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) setIsSidebarOpen(false);
      else setIsSidebarOpen(true);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 1. Fetch User Session
  const fetchUser = useCallback(async (showLoading = false) => {
    console.log('fetchUser ejecutado');
    if (showLoading) setIsLoadingUser(true);
    try {
      const { data } = await api.get('/admin-auth/me');
      setCurrentUser(data.user);
    } catch (error) {
      console.error("Session check failed", error);
      onBackToRegister();
    } finally {
      setIsLoadingUser(false);
    }
  }, [onBackToRegister]);

  useEffect(() => {
    fetchUser(true);
  }, [fetchUser]);

  useEffect(() => {
    if (matchPath(window.location.pathname, '/admin/papers')) {
      setActiveView('committee');
      window.history.replaceState(null, '', buildPath('/admin/committee/papers'));
      return;
    }

    if (matchPath(window.location.pathname, '/admin/configuracion')) {
      setActiveView('settings');
      return;
    }

    if (matchPath(window.location.pathname, '/admin/plantillas-correo')) {
      setActiveView('mailTemplates');
      return;
    }

    if (matchPath(window.location.pathname, '/admin/sysweb-monitor')) {
      setActiveView('syswebMonitor');
      return;
    }

    if (matchPath(window.location.pathname, '/admin/paper-monitor')) {
      setActiveView('paperMonitor');
      return;
    }

    if (matchPath(window.location.pathname, '/admin/registration-monitor')) {
      setActiveView('registrationMonitor');
      return;
    }

    if (matchPath(window.location.pathname, '/admin/audit-logs')) {
      setActiveView('auditLogs');
      return;
    }

    if (matchPath(window.location.pathname, '/admin/committee/dashboard')) {
      setActiveView('committeeDashboard');
      return;
    }

    if (matchPath(window.location.pathname, '/admin/committee/evaluadores')) {
      setActiveView('committeeEvaluators');
      return;
    }

    if (matchPath(window.location.pathname, '/admin/committee/papers') || matchPath(window.location.pathname, '/admin/committee')) {
      setActiveView('committee');
    }
  }, []);

  useEffect(() => {
    if (activeView === 'papers') {
      setActiveView('committee');
      window.history.replaceState(null, '', buildPath('/admin/committee/papers'));
    } else if (activeView === 'settings') {
      window.history.replaceState(null, '', buildPath('/admin/configuracion'));
    } else if (activeView === 'mailTemplates') {
      window.history.replaceState(null, '', buildPath('/admin/plantillas-correo'));
    } else if (activeView === 'syswebMonitor') {
      window.history.replaceState(null, '', buildPath('/admin/sysweb-monitor'));
    } else if (activeView === 'paperMonitor') {
      window.history.replaceState(null, '', buildPath('/admin/paper-monitor'));
    } else if (activeView === 'registrationMonitor') {
      window.history.replaceState(null, '', buildPath('/admin/registration-monitor'));
    } else if (activeView === 'auditLogs') {
      window.history.replaceState(null, '', buildPath('/admin/audit-logs'));
    } else if (activeView === 'committeeDashboard') {
      window.history.replaceState(null, '', buildPath('/admin/committee/dashboard'));
    } else if (activeView === 'committeeEvaluators') {
      window.history.replaceState(null, '', buildPath('/admin/committee/evaluadores'));
    } else if (activeView === 'committee') {
      window.history.replaceState(null, '', buildPath('/admin/committee/papers'));
    } else if (matchPath(window.location.pathname, '/admin/configuracion')) {
      window.history.replaceState(null, '', buildPath('/'));
    } else if (matchPath(window.location.pathname, '/admin/plantillas-correo')) {
      window.history.replaceState(null, '', buildPath('/'));
    } else if (matchPath(window.location.pathname, '/admin/sysweb-monitor')) {
      window.history.replaceState(null, '', buildPath('/'));
    } else if (matchPath(window.location.pathname, '/admin/paper-monitor')) {
      window.history.replaceState(null, '', buildPath('/'));
    } else if (matchPath(window.location.pathname, '/admin/registration-monitor')) {
      window.history.replaceState(null, '', buildPath('/'));
    } else if (matchPath(window.location.pathname, '/admin/audit-logs')) {
      window.history.replaceState(null, '', buildPath('/'));
    } else if (matchPath(window.location.pathname, '/admin/committee/dashboard')) {
      window.history.replaceState(null, '', buildPath('/'));
    } else if (matchPath(window.location.pathname, '/admin/committee/evaluadores')) {
      window.history.replaceState(null, '', buildPath('/'));
    } else if (matchPath(window.location.pathname, '/admin/committee/papers') || matchPath(window.location.pathname, '/admin/committee')) {
      window.history.replaceState(null, '', buildPath('/'));
    }
  }, [activeView]);

  // 2. Fetch Editions
  useEffect(() => {
    if (!currentUser) return;

    const fetchEditions = async () => {
      try {
        const { data } = await api.get('/editions');
        setEditions(data || []);
        // Select the active edition by default, or the first one
        const activeEdition = data?.find((e: Edition) => e.isActive) || data?.[0];
        if (activeEdition && !selectedEditionId) {
          setSelectedEditionId(activeEdition.id);
        }
      } catch (error) {
        console.error("Failed to load editions", error);
        toast.error("Error al cargar ediciones");
      } finally {
        setIsLoadingEditions(false);
      }
    };
    fetchEditions();
  }, [currentUser]);

  // 3. Fetch All Events (then filter by edition)
  useEffect(() => {
    if (!currentUser) return;

    const fetchEvents = async () => {
      try {
        const { data } = await api.get('/events');
        setAllEvents(data || []);
      } catch (error) {
        console.error("Failed to load events", error);
        toast.error("Error al cargar eventos");
      } finally {
        setIsLoadingEvents(false);
      }
    };
    fetchEvents();
  }, [currentUser]);

  // 4. Filter events when edition changes
  useEffect(() => {
    if (!selectedEditionId || allEvents.length === 0 || !currentUser) {
      setFilteredEvents([]);
      return;
    }

    let filtered = allEvents.filter(e => e.editionId === selectedEditionId);

    if (activeView === 'registrations' && selectedAxisFilter !== 'ALL') {
      filtered = filtered.filter(e => e.axis === selectedAxisFilter);
    }

    // Si NO es SuperAdmin, filtrar adicionalmente por eventos asignados
    if (!currentUser.isSuperAdmin && currentUser.assignedEventIds) {
      filtered = filtered.filter(e => currentUser.assignedEventIds?.includes(e.id));
    }

    setFilteredEvents(filtered);

    // Auto-select first event of the edition if current selection is not in this edition
    if (filtered.length > 0 && !filtered.find(e => e.id === selectedEventId)) {
      setSelectedEventId(filtered[0].id);
    } else if (filtered.length === 0) {
      setSelectedEventId('');
    }
  }, [selectedEditionId, selectedAxisFilter, activeView, allEvents, currentUser, selectedEventId]);

  // 5. Fetch Metrics
  useEffect(() => {
    if (!selectedEventId) return;

    const fetchMetrics = async (isPassiveRequest = false) => {
      setIsLoadingMetrics(true);
      try {
        const requestHeaders = isPassiveRequest ? { 'X-Passive-Request': 'true' } : undefined;
        const fetchCount = async (extraParams: Record<string, string>) => {
          const { data } = await api.get(`/registrations`, {
            params: { eventId: selectedEventId, page: 1, limit: 1, ...extraParams },
            headers: requestHeaders,
          });
          return Array.isArray(data) ? data.length : (data?.meta?.total ?? 0);
        };

        const [
          total,
          attendeePaid,
          attendeeMaterialUploaded,
          attendeeCredentialIssued,
          speakerTotal,
          speakerRejected,
          pending,
          pendingReview,
        ] = await Promise.all([
          fetchCount({}),
          fetchCount({ role: 'ATTENDEE', status: 'PAID' }),
          fetchCount({ role: 'ATTENDEE', status: 'MATERIAL_UPLOADED' }),
          fetchCount({ role: 'ATTENDEE', status: 'CREDENTIAL_ISSUED' }),
          fetchCount({ role: 'SPEAKER' }),
          fetchCount({ role: 'SPEAKER', status: 'REJECTED' }),
          fetchCount({ status: 'PENDING' }),
          fetchCount({ status: 'PENDING_REVIEW' }),
        ]);

        setMetrics({
          total,
          attendees: attendeePaid + attendeeMaterialUploaded + attendeeCredentialIssued,
          speakers: Math.max(speakerTotal - speakerRejected, 0),
          pending: pending + pendingReview,
        });
      } catch (error) {
        console.error("Failed to load metrics", error);
      } finally {
        setIsLoadingMetrics(false);
      }
    };
    fetchMetrics(false);
    // Refresh metrics every minute
    const interval = setInterval(() => fetchMetrics(true), 60000);
    return () => clearInterval(interval);
  }, [selectedEventId]);

  const handleLogout = async () => {
    try {
      await api.post("/admin-auth/logout");
      toast.success("Sesión cerrada correctamente");
      window.location.reload();
    } catch (error) {
      console.error("Logout failed", error);
      toast.error("Error al cerrar sesión");
    }
  };



  // Render Sidebar Items
  const SidebarItem = ({ view, icon: Icon, label, disabled = false }: { view: ViewState, icon: any, label: string, disabled?: boolean }) => (
    <button
      onClick={() => !disabled && setActiveView(view)}
      disabled={disabled}
      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-200 
        ${activeView === view
          ? 'bg-white/10 text-[#c9a227] border-l-4 border-[#c9a227]'
          : 'text-gray-300 hover:bg-white/5 hover:text-white border-l-4 border-transparent'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <Icon className={`h-5 w-5 ${activeView === view ? 'text-[#c9a227]' : 'text-gray-400'}`} />
      <span>{label}</span>
    </button>
  );

  function isPaperMonitorEvent(event: Event): boolean {
    return event.axis === PAPER_MONITOR_AXIS && !SPECIAL_GENERAL_ACCESS_SYSWEB_IDS.has(String(event.syswebId || ''));
  }

  if (isLoadingUser || isLoadingEditions || isLoadingEvents) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-[#1e3a5f]" />
          <p className="font-medium text-gray-500">Cargando sistema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">

      {/* Sidebar Overlay for Mobile */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-0 lg:overflow-hidden'}
          bg-[#0f2744] flex flex-col shadow-xl
        `}
        style={{ width: isSidebarOpen ? '16rem' : '0' }}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center px-6 bg-[#0a1a30] border-b border-white/10">
          <img
            src={`${import.meta.env.BASE_URL}escudo-icono.svg`}
            alt="UNACH"
            className="h-8 w-auto mr-3 filter brightness-0 invert"
          />
          <div>
            <h1 className="text-white font-bold text-lg leading-none">UNACH</h1>
            <span className="text-xs text-[#c9a227]">Admin System</span>
          </div>
        </div>

        {/* User Info Short (Mobile) */}
        <div className="lg:hidden p-4 border-b border-white/10 bg-[#1e3a5f]/50">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-[#c9a227] flex items-center justify-center text-white font-bold text-xs">
              {currentUser?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-white truncate">{currentUser?.name}</p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 py-6 space-y-1 overflow-y-auto">
          <SidebarItem view="overview" icon={LayoutDashboard} label="Dashboard" />

          {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.VIEW_REGISTRATIONS)) && (
            <SidebarItem view="registrations" icon={Users} label="Registros" />
          )}

          {/* LEGACY HIDDEN: "Evaluación de ponencias" fue reemplazado por "Ponencias Comité".
          {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.REVIEW_PAPERS)) && (
            <SidebarItem view="papers" icon={FileText} label="Evaluación de ponencias" />
          )}
          */}

          {/* TODO: Implementar modulo de Validacion QR antes de volver a mostrar en sidebar
          {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.VALIDATE_ACCESS)) && (
            <SidebarItem view="validation" icon={QrCode} label="Validación QR" />
          )}
          */}

          {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.MANAGE_COMMITTEE)) && (
            <SidebarItem view="committeeDashboard" icon={LayoutDashboard} label="Dashboard Comité" />
          )}

          {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.MANAGE_COMMITTEE)) && (
            <SidebarItem view="committeeEvaluators" icon={Users} label="Evaluadores" />
          )}

          {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.MANAGE_COMMITTEE)) && (
            <SidebarItem view="committee" icon={FileText} label="Ponencias Comité" />
          )}

          {(currentUser?.isSuperAdmin || hasGlobalHelpdesk || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.VIEW_HELPDESK)) && (
            <SidebarItem view="helpdesk" icon={LifeBuoy} label="Mesa de Ayuda" />
          )}

          {(currentUser?.isSuperAdmin ||
            hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.MANAGE_EVENTS) ||
            hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.MANAGE_USERS) ||
            canViewSettings ||
            canViewSyswebMonitor ||
            canViewPaperMonitor ||
            canViewRegistrationMonitor ||
            canViewAuditLogs) && (
              <div className="pt-4 pb-2 px-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Gestión</p>
              </div>
            )}

          {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.MANAGE_EVENTS)) && (
            <SidebarItem view="events" icon={Calendar} label="Gestión Eventos" />
          )}

          {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.MANAGE_USERS)) && (
            <SidebarItem view="users" icon={Shield} label="Usuarios Sistema" />
          )}

          {canViewSettings && (
            <SidebarItem view="settings" icon={Settings} label="Configuración" />
          )}

          {canManageSettings && (
            <SidebarItem view="mailTemplates" icon={Mail} label="Plantillas de correo" />
          )}

          {canViewSyswebMonitor && (
            <SidebarItem view="syswebMonitor" icon={MonitorCog} label="Monitor SYSWEB" />
          )}

          {canViewPaperMonitor && (
            <SidebarItem view="paperMonitor" icon={FileSearch} label="Monitor de Ponencias" />
          )}

          {canViewRegistrationMonitor && (
            <SidebarItem view="registrationMonitor" icon={ClipboardList} label="Monitor de Registros" />
          )}

          {canViewAuditLogs && (
            <SidebarItem view="auditLogs" icon={BookOpenText} label="Bitácora de auditoría" />
          )}

          {currentUser?.isSuperAdmin && (
            <SidebarItem view="simulation" icon={Database} label="Simulador" />
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-white/10">
          <p className="text-xs text-center text-gray-500">v2.1.0 UNACH System</p>
        </div>
      </aside>


      {/* MAIN CONTENT WRAPPER */}
      <div className="flex-1 flex flex-col h-full w-full overflow-hidden relative">

        {/* TOP HEADER */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 z-30 shadow-sm">

          {/* Left: Toggle & Title */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-gray-600 hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </Button>

            {!isMobile && (
              <h2 className="text-xl font-bold text-[#0f2744] hidden sm:block">
                {activeView === 'overview' && 'Panel Principal'}
                {activeView === 'registrations' && 'Base de Registros'}
                {activeView === 'validation' && 'Validación de Accesos'}
                {activeView === 'events' && 'Gestión de Eventos'}
                {activeView === 'users' && 'Usuarios del Sistema'}
                {activeView === 'settings' && 'Configuración Global'}
                {activeView === 'mailTemplates' && 'Plantillas de correo'}
                {activeView === 'syswebMonitor' && 'Monitor SYSWEB'}
                {activeView === 'paperMonitor' && 'Monitor de Ponencias'}
                {activeView === 'registrationMonitor' && 'Monitor de Registros'}
                {activeView === 'auditLogs' && 'Bitácora de Auditoría'}
                {activeView === 'papers' && 'Ponencias del Comite'}
                {activeView === 'committee' && 'Ponencias del Comite'}
                {activeView === 'committeeDashboard' && 'Dashboard Comité Evaluador'}
                {activeView === 'committeeEvaluators' && 'Evaluadores del Evento'}
                {activeView === 'helpdesk' && 'Mesa de Ayuda'}
                {activeView === 'simulation' && 'Simulador'}
              </h2>
            )}
          </div>

          {/* Right: Event Selector & User */}
          <div className="flex items-center gap-4">

            {/* Year Selector */}
            <div className="hidden md:flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase">Año:</span>
              <Select value={selectedEditionId} onValueChange={setSelectedEditionId}>
                <SelectTrigger className="w-[100px] bg-gray-50 border-gray-200 text-[#0f2744] font-bold h-9">
                  <SelectValue placeholder="Año" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {editions.map(ed => (
                    <SelectItem key={ed.id} value={ed.id} className="text-gray-700">
                      {ed.year} {ed.isActive && '✓'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Event Selector */}
            <div className="hidden md:flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase">Evento:</span>
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className="w-[260px] bg-gray-50 border-gray-200 text-[#0f2744] font-medium h-9">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {filteredEvents.map(e => (
                    <SelectItem key={e.id} value={e.id} className="text-gray-700">
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activeView === 'registrations' && (
              <div className="hidden md:flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase">Eje:</span>
                <Select value={selectedAxisFilter} onValueChange={(value: AxisFilter) => setSelectedAxisFilter(value)}>
                  <SelectTrigger className="w-[210px] bg-gray-50 border-gray-200 text-[#0f2744] font-medium h-9">
                    <SelectValue placeholder="Eje temático" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="cientifico">Científico</SelectItem>
                    <SelectItem value="cultural">Cultural</SelectItem>
                    <SelectItem value="innovacion">Empresarial / Innovación</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="h-6 w-px bg-gray-200 mx-2 hidden md:block"></div>

            {/* Profile Dropdown */}
            <div className="flex items-center gap-3">
              <div className="text-right hidden md:block">
                <p className="text-sm font-bold text-[#0f2744]">{currentUser?.name}</p>
                <p className="text-xs text-gray-500">{currentUser?.isSuperAdmin ? 'Super Admin' : 'Admin'}</p>
              </div>
              <div className="h-9 w-9 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white font-bold text-sm ring-2 ring-gray-100 cursor-pointer">
                {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleLogout}
                className="text-gray-500 hover:text-red-600"
                title="Cerrar Sesión"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* CONTENT SCROLLABLE AREA */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-8">
          <div className="max-w-7xl mx-auto space-y-6">

            {/* Mobile Event Selector */}
            <div className="md:hidden mb-6 space-y-3">
              {activeView === 'registrations' && (
                <Select value={selectedAxisFilter} onValueChange={(value: AxisFilter) => setSelectedAxisFilter(value)}>
                  <SelectTrigger className="w-full bg-white border-gray-200">
                    <SelectValue placeholder="Eje temático" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="cientifico">Científico</SelectItem>
                    <SelectItem value="cultural">Cultural</SelectItem>
                    <SelectItem value="innovacion">Empresarial / Innovación</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className="w-full bg-white border-gray-200">
                  <SelectValue placeholder="Seleccione Evento" />
                </SelectTrigger>
                <SelectContent>
                  {filteredEvents.map(e => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            {/* VIEWS */}
            {activeView === 'overview' && (
              <div className="space-y-8 animate-in fade-in-50 duration-500">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {(currentUser?.isSuperAdmin || hasPermission(currentUser?.permissionsByEvent?.[selectedEventId], PERMISSIONS.VIEW_DASHBOARD)) ? (
                    <>
                      <MetricCard
                        title="Total Registros"
                        value={isLoadingMetrics ? '...' : metrics.total}
                        icon={Users}
                        variant="primary"
                        subtitle="En este evento"
                      />
                      <MetricCard
                        title="Asistentes"
                        value={isLoadingMetrics ? '...' : metrics.attendees}
                        icon={UserCheck}
                        variant="blue"
                        subtitle="Pagados/Confirmados"
                      />
                      <MetricCard
                        title="Ponentes"
                        value={isLoadingMetrics ? '...' : metrics.speakers}
                        icon={Mic2}
                        variant="gold"
                        subtitle="Con ponencia activa"
                      />
                      <MetricCard
                        title="Pendientes"
                        value={isLoadingMetrics ? '...' : metrics.pending}
                        icon={Clock}
                        variant="gray"
                        subtitle="Pre-registros incompletos"
                      />
                    </>
                  ) : (
                    <Card className="col-span-full shadow-sm border-gray-100 bg-blue-50/50">
                      <CardContent className="p-12 flex flex-col items-center justify-center text-center">
                        <LockIcon className="h-10 w-10 text-blue-300 mb-4" />
                        <h3 className="text-lg font-semibold text-blue-900">Vista de Métricas Restringida</h3>
                        <p className="text-sm text-blue-600 max-w-sm mt-2">
                          No tienes permisos asignados para visualizar el dashboard estadístico de este evento.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <Card className="shadow-sm border-gray-100">
                    <CardContent className="p-6 min-h-[300px] flex flex-col items-center justify-center text-center">
                      <div className="p-4 rounded-full bg-blue-50 mb-4">
                        <Users className="h-8 w-8 text-[#1e3a5f]" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Actividad Reciente</h3>
                      <p className="text-sm text-gray-500 max-w-xs mt-2">
                        Gráfico de inscripciones por día (Próximamente con Recharts)
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="shadow-sm border-gray-100">
                    <CardContent className="p-6 min-h-[300px] flex flex-col items-center justify-center text-center">
                      <div className="p-4 rounded-full bg-yellow-50 mb-4">
                        <QrCode className="h-8 w-8 text-[#c9a227]" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Validaciones en Sitio</h3>
                      <p className="text-sm text-gray-500 max-w-xs mt-2">
                        Estadísticas de control de acceso en tiempo real
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {activeView === 'registrations' && (
              <div className="animate-in fade-in-50 duration-300">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="font-bold text-lg text-[#0f2744]">Base de Datos de Inscritos</h3>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">Exportar Excel</Button>
                    </div>
                  </div>
                  <div className="p-0">
                    <RegistrationTable eventId={selectedEventId} axisFilter={selectedAxisFilter} />
                  </div>
                </div>
              </div>
            )}

            {activeView === 'events' && (
              <EventsManagementView
                editions={editions}
                selectedEditionId={selectedEditionId}
                onEditionChange={setSelectedEditionId}
                onEditionCreated={async () => {
                  // Reload editions when a new one is created
                  try {
                    const { data } = await api.get('/editions');
                    setEditions(data || []);
                  } catch (error) {
                    console.error('Error reloading editions:', error);
                  }
                }}
              />
            )}

            {activeView === 'users' && (
              <UsersManagementView
                events={allEvents}
                onPermissionsUpdated={() => fetchUser(false)}
              />
            )}

            {activeView === 'papers' && (
              <CommitteePapersPage eventId={selectedEventId} />
            )}

            {activeView === 'committee' && (
              <CommitteePapersPage eventId={selectedEventId} />
            )}

            {activeView === 'committeeDashboard' && (
              <CommitteeDashboardPage
                eventId={selectedEventId}
                onGoEvaluators={() => setActiveView('committeeEvaluators')}
                onGoPapers={() => setActiveView('committee')}
              />
            )}

            {activeView === 'committeeEvaluators' && (
              <CommitteeEvaluatorsPage
                eventId={selectedEventId}
                canSendRecognitions={canSendEvaluatorRecognitions}
              />
            )}

            {activeView === 'helpdesk' && (
              <HelpDeskView selectedEventId={selectedEventId} />
            )}

            {activeView === 'simulation' && (
              <SimulationView />
            )}

            {activeView === 'settings' && (
              <SettingsPage
                canManage={canManageSettings}
                isSuperAdmin={Boolean(currentUser?.isSuperAdmin)}
                selectedEventId={selectedEventId}
              />
            )}

            {activeView === 'mailTemplates' && (
              <MailTemplatesPage
                canManage={canManageSettings}
                isSuperAdmin={Boolean(currentUser?.isSuperAdmin)}
                selectedEventId={selectedEventId}
                availableEvents={filteredEvents.map((event) => ({ id: event.id, name: event.name }))}
              />
            )}

            {activeView === 'syswebMonitor' && (
              <SyswebMonitorPage
                canView={canViewSyswebMonitor}
                selectedEventId={selectedEventId}
                availableEvents={filteredEvents.map((event) => ({ id: event.id, name: event.name }))}
              />
            )}

            {activeView === 'paperMonitor' && (
              <PaperEvaluationMonitorPage
                canView={canViewPaperMonitor}
                selectedEventId={selectedEventId}
                availableEvents={paperMonitorEvents.map((event) => ({ id: event.id, name: event.name }))}
              />
            )}

            {activeView === 'registrationMonitor' && (
              <RegistrationMonitorPage
                canView={canViewRegistrationMonitor}
                isSuperAdmin={Boolean(currentUser?.isSuperAdmin)}
                selectedEventId={selectedEventId}
                availableEvents={registrationMonitorEvents.map((event) => ({ id: event.id, name: event.name }))}
              />
            )}

            {activeView === 'auditLogs' && (
              <AuditLogsPage canView={canViewAuditLogs} />
            )}

            {activeView === 'validation' && (
              <div className="flex flex-col items-center justify-center h-[50vh] text-center p-8 bg-white rounded-xl border border-dashed border-gray-300">
                <div className="p-6 rounded-full bg-gray-100 mb-4">
                  {activeView === 'validation' && <QrCode className="h-10 w-10 text-gray-400" />}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Módulo en Construcción</h3>
                <p className="text-gray-500 max-w-md">
                  Estamos trabajando en la funcionalidad de <strong>{activeView}</strong>. Estará disponible en el próximo sprint.
                </p>
              </div>
            )}

          </div>
        </main >

      </div >
      <SessionDebugPanel />
    </div >
  );
}



